import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch } from 'store/app/store';

import { SnackbarMessage, SnackbarType } from './Snackbar.types';

type State = {
    isSnackbarOpen: boolean;
    snackbarQueue: SnackbarMessage[];
    snackbarMessage: SnackbarMessage;
};

const LONG_DURATION = 5000;

const INITIAL_MESSAGE = { duration: LONG_DURATION, key: 0, message: '', type: SnackbarType.Info };

const initialState: State = {
    isSnackbarOpen: false,
    snackbarMessage: INITIAL_MESSAGE,
    snackbarQueue: []
};

const processSnackbarQueueImpl = (state: State): State => {
    if (state.snackbarQueue.length) {
        const localSnackbarQueue = [...state.snackbarQueue];
        return { ...state, isSnackbarOpen: true, snackbarMessage: localSnackbarQueue.shift() as SnackbarMessage, snackbarQueue: localSnackbarQueue };
    }
    return { ...initialState };
};

const snackbarSlice = createSlice({
    initialState,
    name: 'snackbar',
    reducers: {
        addSnackbar(state, action: PayloadAction<SnackbarMessage>): State {
            const localSnackbarQueue = [...state.snackbarQueue];
            localSnackbarQueue.push(action.payload);
            if (state.isSnackbarOpen) {
                return { ...state, snackbarQueue: localSnackbarQueue };
            }
            return {
                ...state,
                isSnackbarOpen: true,
                snackbarMessage: localSnackbarQueue.shift() as SnackbarMessage,
                snackbarQueue: localSnackbarQueue
              };
        },
        closeSnackbar(state): State {
            return { ...state, isSnackbarOpen: false };
        },
        exitSnackbar(state): State {
            return processSnackbarQueueImpl(state);
        }
    }
});

export const {
    addSnackbar,
    closeSnackbar,
    exitSnackbar
} = snackbarSlice.actions;

export const showLongInfoSnackbar = (
    dispatch: AppDispatch,
    message: string
): void => {
    dispatch(
        addSnackbar({
            duration: LONG_DURATION,
            key: new Date().getTime(),
            message,
            type: SnackbarType.Info
        })
    );
};

export const showSuccessSnackbar = (
    dispatch: AppDispatch,
    message: string
): void => {
    dispatch(
        addSnackbar({
            duration: LONG_DURATION,
            key: new Date().getTime(),
            message,
            type: SnackbarType.Success
        })
    );
};

export const showWarningSnackbar = (
    dispatch: AppDispatch,
    message: string
): void => {
    dispatch(
        addSnackbar({
            duration: LONG_DURATION,
            key: new Date().getTime(),
            message,
            type: SnackbarType.Warning
        })
    );
};

export const showErrorSnackbar = (
    dispatch: AppDispatch,
    message: string
): void => {
    dispatch(
        addSnackbar({
            duration: LONG_DURATION,
            key: new Date().getTime(),
            message,
            type: SnackbarType.Error
        })
    );
};

export const showValidationErrorsSnackbar = (
    dispatch: AppDispatch,
    errors: string[]
): void => {
    const message = (
        <ul>
            {errors.map((error, index) => (
                <li key={index}>{error}</li>
            ))}
        </ul>
    );
    
    dispatch(
        addSnackbar({
            duration: LONG_DURATION,
            key: new Date().getTime(),
            message,
            type: SnackbarType.Error
        })
    );
};

export default snackbarSlice.reducer;
